import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import {
  CatalogGraphPage,
  catalogGraphPlugin,
} from '@backstage/plugin-catalog-graph';

import { CostInsightsPage } from '@backstage/plugin-cost-insights';
// import { CostInsightsPage as LogCostInsightsPage } from '@internal/plugin-log-cost-insights';

import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';

import { ExplorePage, explorePlugin } from '@backstage/plugin-explore';

import { OpsgeniePage } from '@k-phoen/backstage-plugin-opsgenie';

import { ToolboxPage } from '@drodil/backstage-plugin-toolbox';

import {
  ScaffolderPage,
  ScaffolderFieldExtensions,
  scaffolderPlugin
} from '@backstage/plugin-scaffolder';
import {
  ComponentPickerFieldExtension,
  TeamPickerFieldExtension,
} from './scaffolder';

import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage/plugin-tech-radar';
import {
  DefaultTechDocsHome,
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  ProxiedSignInPage,
  SignInPage,
} from '@backstage/core-components';
import { AnnouncementsPage } from '@k-phoen/backstage-plugin-announcements';
import { createApp } from '@backstage/app-defaults';
import { FlatRoutes, AppRouter } from '@backstage/core-app-api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { ArgusUiPage } from '@internal/plugin-argus-ui';
import { IdeCodeScanPage } from '@imaginelearning/ide-code-scan-frontend';
import { CustomCatalogPage } from './components/catalog/CustomCatalogPage';
import { ILDarkTheme, ILLightTheme } from './components/Root/Theme';
import { ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import LightIcon from '@material-ui/icons/WbSunny';
import Brightness7 from '@material-ui/icons/Brightness7';
import { configApiRef, discoveryApiRef, IdentityApi, oktaAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { setTokenCookie } from './cookieAuth';

const oktaProvider = {
  id: 'okta-auth-provider',
  title: 'Okta',
  message: 'Sign in using Okta',
  apiRef: oktaAuthApiRef,
}

const app = createApp({
  apis,
  themes: [{
    id: 'il-light-theme',
    title: 'Light Theme',
    variant: 'light',
    icon: <LightIcon />,
    Provider: ({ children }) => (
      <ThemeProvider theme={ILLightTheme}>
        <CssBaseline>{children}</CssBaseline>
      </ThemeProvider>
    ),
  },
  {
    id: 'il-dark-theme',
    title: 'Dark Theme',
    variant: 'dark',
    icon: <Brightness7 />,
    Provider: ({ children }) => (
      <ThemeProvider theme={ILDarkTheme}>
        <CssBaseline>{children}</CssBaseline>
      </ThemeProvider>
    ),
  },
  ],
  components: {
    SignInPage: props => {
      const discoveryApi = useApi(discoveryApiRef);
      if (useApi(configApiRef).getString('auth.environment') === 'development') {
        return <ProxiedSignInPage {...props} provider="oauth2proxy" />
      }
      return (
        <SignInPage
          auto
          align="center"
          onSignInSuccess={async (identityApi: IdentityApi) => {
            setTokenCookie(
              await discoveryApi.getBaseUrl('cookie'),
              identityApi,
            );
            props.onSignInSuccess(identityApi);
          }}
          providers={[oktaProvider]}
        />
      )
    },
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(catalogGraphPlugin.externalRoutes, {
      catalogEntity: catalogPlugin.routes.catalogEntity,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(explorePlugin.externalRoutes, {
      catalogEntity: catalogPlugin.routes.catalogEntity,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} >
      <CustomCatalogPage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons />
    </Route>
    <Route path="/toolbox" element={<ToolboxPage />} />
    <Route path="/cost-insights" element={<CostInsightsPage />} />
    <Route path="/opsgenie" element={<OpsgeniePage />} />

    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <ComponentPickerFieldExtension />
        <TeamPickerFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>

    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/explore" element={<ExplorePage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route
      path="/catalog-import"
      element={<CatalogImportPage />}
    />
    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route path="/search" element={<SearchPage />} />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/ide-code-scan-frontend" element={<IdeCodeScanPage />} />
    <Route path="/argus-ui" element={<ArgusUiPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
